import {
  CAMPAIGN_FUNDING_MAX_CENTS,
  MAX_OFFERS_PER_CAMPAIGN,
  OFFER_MAX_BILLABLE_CENTS,
  OFFER_MAX_REWARD_COINS,
  OWNER_OFFER_KIND_MESSAGE,
  OfferTerms,
  isOwnerOfferKind,
  isOwnerOfferReward,
  type CampaignOfferView,
  type OfferKind,
} from "@townsy/contracts";
import { DEFAULT_DAILY_CAP_COINS } from "./copy";

/**
 * The advertiser's task editor as plain data (ADR-0011 #1-#3, docs/07 section 3): text fields are kept
 * as typed, money is entered in dollars and converted to integer cents without floating point, and the
 * result is validated with friendly messages and then with the shared `OfferTerms` contract. The server
 * validates again (and moderates the text); nothing here is authority.
 */

export const DEFAULTS = {
  rewardCoins: "10",
  /** Development default: 5 cents per completion (ADR-0011 #2). */
  billableDollars: "0.05",
  attemptTtlMinutes: "10",
  minReturnSeconds: "15",
  maxTries: "2",
} as const;

export interface OfferDraft {
  /** Draft-local key (React list key). */
  key: string;
  /** Existing offer being edited, or null for a new task. */
  offerId: string | null;
  kind: OfferKind;
  title: string;
  instructions: string;
  rewardCoins: string;
  /** Dollars per completion as typed ("0.05"). */
  billableDollars: string;
  attemptTtlMinutes: string;
  /** Empty = no daily limit. */
  dailyCompletionLimit: string;
  minReturnSeconds: string;
  explanation: string;
  question: string;
  choices: string[];
  correctChoice: number | null;
  maxTries: string;
  conversionType: string;
  /** The explicit "I approve incentivized visits and this exact task" tick, for THIS version of the terms. */
  consent: boolean;
  /** Changed since loaded (existing offers are only sent when changed). */
  dirty: boolean;
}

export type OfferField =
  | "title"
  | "instructions"
  | "rewardCoins"
  | "billableDollars"
  | "attemptTtlMinutes"
  | "dailyCompletionLimit"
  | "minReturnSeconds"
  | "explanation"
  | "question"
  | "choices"
  | "correctChoice"
  | "maxTries"
  | "conversionType"
  | "consent"
  | "kind"
  | "terms";

export type OfferErrors = Partial<Record<OfferField, string>>;

let seq = 0;
const nextKey = () => `offer-${++seq}`;

export function emptyOfferDraft(kind: OfferKind = "website_visit"): OfferDraft {
  return {
    key: nextKey(),
    offerId: null,
    kind,
    title: "",
    instructions: "",
    rewardCoins: DEFAULTS.rewardCoins,
    billableDollars: DEFAULTS.billableDollars,
    attemptTtlMinutes: DEFAULTS.attemptTtlMinutes,
    dailyCompletionLimit: "",
    minReturnSeconds: DEFAULTS.minReturnSeconds,
    explanation: "",
    question: "",
    choices: ["", ""],
    correctChoice: null,
    maxTries: DEFAULTS.maxTries,
    conversionType: "signup",
    consent: false,
    dirty: true,
  };
}

/** An existing offer's current terms as an editable draft (consent must be ticked again for a change). */
export function draftFromOffer(offer: Pick<CampaignOfferView, "offerId" | "kind" | "current">): OfferDraft {
  const t = offer.current.terms;
  return {
    key: nextKey(),
    offerId: offer.offerId,
    kind: offer.kind,
    title: t.title,
    instructions: t.instructions,
    rewardCoins: String(t.rewardCoins),
    billableDollars: centsToDollars(t.billableCents),
    attemptTtlMinutes: String(Math.round(t.attemptTtlSeconds / 60)),
    dailyCompletionLimit: t.dailyCompletionLimit === null ? "" : String(t.dailyCompletionLimit),
    minReturnSeconds: String(t.minReturnSeconds ?? 15),
    explanation: t.explanation ?? "",
    question: t.question ?? "",
    choices: t.choices && t.choices.length >= 2 ? [...t.choices] : ["", ""],
    correctChoice: t.correctChoice,
    maxTries: String(t.maxTries ?? 2),
    conversionType: t.conversionType ?? "signup",
    consent: false,
    dirty: false,
  };
}

/** Apply a field edit (any change marks the draft dirty and clears the consent tick for the new terms). */
export function editDraft(d: OfferDraft, patch: Partial<Omit<OfferDraft, "key" | "offerId" | "dirty">>): OfferDraft {
  const consentOnly = Object.keys(patch).length === 1 && "consent" in patch;
  if (consentOnly) return { ...d, consent: Boolean(patch.consent) };
  return { ...d, ...patch, dirty: true, consent: "consent" in patch ? Boolean(patch.consent) : false };
}

// ---------------------------------------------------------------- money and numbers (no floating point)

/** "0.05" -> 5, "12" -> 1200, "1.5" -> 150; null when not a plain dollar amount with at most 2 decimals. */
export function dollarsToCents(input: string): number | null {
  const s = input.trim().replace(/^\$/, "").replace(/,/g, "");
  const m = /^(\d{1,7})(?:\.(\d{1,2}))?$/.exec(s) ?? /^()\.(\d{1,2})$/.exec(s);
  if (!m) return null;
  const whole = m[1] ? Number(m[1]) : 0;
  const frac = m[2] ? Number(m[2].padEnd(2, "0")) : 0;
  const cents = whole * 100 + frac;
  return Number.isSafeInteger(cents) ? cents : null;
}

export function centsToDollars(cents: number): string {
  const c = Math.max(0, Math.trunc(cents));
  return `${Math.floor(c / 100)}.${String(c % 100).padStart(2, "0")}`;
}

/** A whole number as typed ("10"), or null. */
export function parseWhole(input: string): number | null {
  const s = input.trim();
  if (!/^\d{1,9}$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) ? n : null;
}

// ---------------------------------------------------------------- validation

export type DraftResult = { ok: true; terms: OfferTerms } | { ok: false; errors: OfferErrors };

/** ADR-0034: the reward error of an owner's new or changed offer. */
export const OWNER_REWARD_ERROR = "Choose 5 or 10 coins.";

/**
 * Validate one task. `isHouse`: Townsy house campaigns allow quizzes only. `dailyCapCoins`: a reward
 * can never exceed the resident's daily sponsored-coin cap. `free` (ADR-0029: offers are free for building owners):
 * there is no price; the terms carry 0 cents (the server stores 0 whatever is sent). `owner` (ADR-0034, founder
 * 2026-10-22): a building owner's new or changed offer is a website visit or a question giving 5 or 10 coins.
 */
export function validateOfferDraft(d: OfferDraft, opts: { isHouse?: boolean; dailyCapCoins?: number; free?: boolean; owner?: boolean } = {}): DraftResult {
  const errors: OfferErrors = {};
  const cap = Math.min(opts.dailyCapCoins ?? DEFAULT_DAILY_CAP_COINS, OFFER_MAX_REWARD_COINS);
  if (opts.isHouse && d.kind !== "quiz") errors.kind = "Townsy house campaigns can only use the on-Townsy quiz.";
  if (opts.owner && !isOwnerOfferKind(d.kind)) errors.kind = OWNER_OFFER_KIND_MESSAGE;
  const title = d.title.trim();
  if (!title) errors.title = "Give the task a short title.";
  else if (title.length > 60) errors.title = "At most 60 characters.";
  const instructions = d.instructions.trim();
  if (!instructions) errors.instructions = "Tell residents exactly what to do.";
  else if (instructions.length > 280) errors.instructions = "At most 280 characters.";

  const reward = parseWhole(d.rewardCoins);
  if (opts.owner && (reward === null || !isOwnerOfferReward(reward))) errors.rewardCoins = OWNER_REWARD_ERROR;
  else if (reward === null || reward < 1) errors.rewardCoins = "A whole number of coins, at least 1.";
  else if (reward > cap) errors.rewardCoins = `At most ${cap} coins (the daily sponsored-coin limit of a resident).`;

  const billable = opts.free ? 0 : dollarsToCents(d.billableDollars);
  if (!opts.free) {
    if (billable === null) errors.billableDollars = "An amount in dollars, for example 0.05.";
    else if (billable < 1) errors.billableDollars = "At least $0.01 per completion.";
    else if (billable > OFFER_MAX_BILLABLE_CENTS) errors.billableDollars = `At most $${centsToDollars(OFFER_MAX_BILLABLE_CENTS)} per completion.`;
  }

  const ttl = parseWhole(d.attemptTtlMinutes);
  if (ttl === null || ttl < 2 || ttl > 60) errors.attemptTtlMinutes = "Between 2 and 60 minutes.";

  let daily: number | null = null;
  if (d.dailyCompletionLimit.trim()) {
    daily = parseWhole(d.dailyCompletionLimit);
    if (daily === null || daily < 1 || daily > 100_000) errors.dailyCompletionLimit = "Leave empty for no limit, or 1 to 100,000 per UTC day.";
  }

  let minReturn = 15;
  let maxTries = 2;
  const choices = d.choices.map((c) => c.trim());
  if (d.kind === "website_visit") {
    const m = parseWhole(d.minReturnSeconds);
    if (m === null || m < 15 || m > 600) errors.minReturnSeconds = "Between 15 and 600 seconds.";
    else minReturn = m;
  } else if (d.kind === "quiz") {
    const explanation = d.explanation.trim();
    if (!explanation) errors.explanation = "A short explanation of your brand or product that contains the answer.";
    else if (explanation.length > 280) errors.explanation = "At most 280 characters.";
    const question = d.question.trim();
    if (!question) errors.question = "Ask one simple question.";
    else if (question.length > 200) errors.question = "At most 200 characters.";
    if (choices.length < 2 || choices.length > 4) errors.choices = "Offer 2 to 4 answers.";
    else if (choices.some((c) => !c)) errors.choices = "Every answer needs text.";
    else if (choices.some((c) => c.length > 80)) errors.choices = "Each answer: at most 80 characters.";
    else if (new Set(choices.map((c) => c.toLowerCase())).size !== choices.length) errors.choices = "The answers must be different.";
    if (d.correctChoice === null || d.correctChoice < 0 || d.correctChoice >= choices.length) errors.correctChoice = "Mark the correct answer.";
    const t = parseWhole(d.maxTries);
    if (t === null || t < 1 || t > 3) errors.maxTries = "1 to 3 tries.";
    else maxTries = t;
  } else {
    if (!/^[a-z][a-z0-9_]{1,31}$/.test(d.conversionType.trim())) errors.conversionType = "Lower-case letters, digits and _ (2-32 characters, starting with a letter), e.g. signup.";
  }
  if (!d.consent) errors.consent = "Tick the consent for this exact task.";
  if (Object.keys(errors).length > 0) return { ok: false, errors };

  const base = {
    title,
    instructions,
    rewardCoins: reward!,
    billableCents: billable!,
    attemptTtlSeconds: ttl! * 60,
    dailyCompletionLimit: daily,
    consent: true as const,
  };
  const raw =
    d.kind === "website_visit"
      ? { kind: d.kind, ...base, minReturnSeconds: minReturn }
      : d.kind === "quiz"
        ? { kind: d.kind, ...base, explanation: d.explanation.trim(), question: d.question.trim(), choices, correctChoice: d.correctChoice!, maxTries }
        : { kind: d.kind, ...base, conversionType: d.conversionType.trim() };
  const parsed = OfferTerms.safeParse(raw);
  if (!parsed.success) return { ok: false, errors: { terms: parsed.error.issues[0]?.message ?? "These terms are not valid." } };
  return { ok: true, terms: parsed.data };
}

// ---------------------------------------------------------------- campaign drafts

export interface CampaignDraft {
  title: string;
  propertyId: string;
  creativeId: string;
  /** YYYY-MM-DD (UTC end of that day) or empty for no end. */
  endsOn: string;
  offers: OfferDraft[];
}

export type CampaignErrors = Partial<Record<"title" | "propertyId" | "creativeId" | "endsOn" | "offers", string>> & { offerErrors?: Record<string, OfferErrors> };

/** "2026-12-31" -> "2026-12-31T23:59:59.000Z" (campaigns end at the end of a UTC day); null when empty / invalid. */
export function endOfUtcDay(date: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const t = Date.parse(`${date}T23:59:59.000Z`);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}

export function validateCampaignDraft(
  d: CampaignDraft,
  opts: {
    now: number;
    isHouse?: boolean;
    requireStore?: boolean;
    dailyCapCoins?: number;
    /** ADR-0029: a free campaign (no price). */
    free?: boolean;
    /** ADR-0034: an owner's campaign: new or changed tasks are a website visit or a question with 5 or 10 coins. */
    owner?: boolean;
  },
):{ ok: true; title: string; endsAt: string | null; offers: Array<{ offerId: string | null; terms: OfferTerms }> } | { ok: false; errors: CampaignErrors } {
  const errors: CampaignErrors = {};
  const title = d.title.trim();
  if (!title) errors.title = "Give the campaign a title (residents see the task titles; this one is for you and the reviewers).";
  else if (title.length > 60) errors.title = "At most 60 characters.";
  if (opts.requireStore !== false && !d.propertyId) errors.propertyId = opts.isHouse ? "Choose a Townsy building." : "Choose one of your stores.";
  if (!opts.isHouse && opts.requireStore !== false && !d.creativeId) errors.creativeId = "Choose the approved design currently shown on that store.";
  let endsAt: string | null = null;
  if (d.endsOn.trim()) {
    endsAt = endOfUtcDay(d.endsOn.trim());
    if (!endsAt) errors.endsOn = "Use a date (YYYY-MM-DD) or leave empty.";
    else if (Date.parse(endsAt) <= opts.now) errors.endsOn = "The end date must be in the future.";
  }
  if (d.offers.length < 1) errors.offers = "Add at least one task.";
  else if (d.offers.length > MAX_OFFERS_PER_CAMPAIGN) errors.offers = `At most ${MAX_OFFERS_PER_CAMPAIGN} tasks per campaign.`;
  const offerErrors: Record<string, OfferErrors> = {};
  const offers: Array<{ offerId: string | null; terms: OfferTerms }> = [];
  for (const o of d.offers) {
    // Unchanged existing tasks are not sent (and need no new consent).
    if (o.offerId && !o.dirty) continue;
    const r = validateOfferDraft(o, { isHouse: opts.isHouse, dailyCapCoins: opts.dailyCapCoins, free: opts.free, owner: opts.owner });
    if (r.ok) offers.push({ offerId: o.offerId, terms: r.terms });
    else offerErrors[o.key] = r.errors;
  }
  if (Object.keys(offerErrors).length > 0) errors.offerErrors = offerErrors;
  if (Object.keys(errors).length > 0) return { ok: false, errors };
  return { ok: true, title, endsAt, offers };
}

/** House campaign budget (cents) as typed in dollars; 1 cent to the funding maximum. */
export function validateHouseBudget(input: string): { ok: true; cents: number } | { ok: false; error: string } {
  const cents = dollarsToCents(input);
  if (cents === null || cents < 1) return { ok: false, error: "An amount in dollars, at least $0.01." };
  if (cents > CAMPAIGN_FUNDING_MAX_CENTS) return { ok: false, error: `At most $${centsToDollars(CAMPAIGN_FUNDING_MAX_CENTS)}.` };
  return { ok: true, cents };
}
